import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, Plus, Loader2,
  ToggleLeft, ToggleRight, User, Timer, CreditCard
} from "lucide-react";
import GameListSidebar from "@/components/registrations/GameListSidebar";
import GameDetailPanel from "@/components/registrations/GameDetailPanel";
import EditGameDialog from "@/components/registrations/EditGameDialog";

export default function GameRegistrationsPage({ evaluator }) {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useLanguage();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [registrationsEnabled, setRegistrationsEnabled] = useState(false);
  const [isTogglingRegistrations, setIsTogglingRegistrations] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  // Helper: calculate Monday 12:01 São Paulo time for a given date string
  const calcMondayDeadline = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    const day = d.getDay(); // 0=Sun, 1=Mon, ...
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + diffToMonday);
    // Format as datetime-local: YYYY-MM-DDTHH:MM
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, "0");
    const dd = String(monday.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T12:01`;
  };

  const [newGame, setNewGame] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      title: "Jogo do Dia",
      date: today,
      time: "19:00",
      location: "",
      max_players: 18,
      guest_deadline: calcMondayDeadline(today),
      scheduled_open: "",
    };
  });
  const [selectedGame, setSelectedGame] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/system-settings`);
      setRegistrationsEnabled(response.data?.registrations_enabled || false);
    } catch (error) {
      console.error("Error loading system settings:", error);
    }
  }, []);

  useEffect(() => {
    fetchGames();
    fetchSystemSettings();
  }, [fetchSystemSettings]); // fetchGames is stable (defined outside useEffect)

  const handleToggleRegistrations = async () => {
    setIsTogglingRegistrations(true);
    try {
      const newValue = !registrationsEnabled;
      await axios.put(`${API}/system-settings`, {
        registrations_enabled: newValue
      });
      setRegistrationsEnabled(newValue);
      toast.success(newValue 
        ? "Inscricoes liberadas para os jogadores!" 
        : "Inscricoes bloqueadas para os jogadores"
      );
    } catch (error) {
      toast.error("Erro ao alterar configuracao");
    } finally {
      setIsTogglingRegistrations(false);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/game-registrations`);
      setGames(response.data);
    } catch (error) {
      toast.error("Erro ao carregar jogos");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGame = async () => {
    if (!newGame.date) {
      toast.error("Selecione uma data");
      return;
    }

    setIsCreating(true);
    try {
      const payload = { ...newGame };
      if (!payload.guest_deadline) {
        delete payload.guest_deadline;
      }
      if (!payload.scheduled_open) {
        delete payload.scheduled_open;
      }
      await axios.post(`${API}/game-registrations`, payload);
      toast.success(payload.scheduled_open 
        ? "Inscricoes agendadas com sucesso!" 
        : "Inscricoes abertas com sucesso!");
      setCreateDialogOpen(false);
      const today = new Date().toISOString().split('T')[0];
      setNewGame({
        title: t('gameRegistrations.defaultGameTitle'),
        date: today,
        time: "19:00",
        location: "",
        max_players: 18,
        guest_deadline: calcMondayDeadline(today),
        scheduled_open: "",
      });
      fetchGames();
    } catch (error) {
      toast.error("Erro ao criar jogo");
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleStatus = async (gameId, isOpen) => {
    try {
      if (isOpen) {
        await axios.put(`${API}/game-registrations/${gameId}/close`);
        toast.success("Inscricoes encerradas");
      } else {
        await axios.put(`${API}/game-registrations/${gameId}/open`);
        toast.success("Inscricoes reabertas");
      }
      fetchGames();
      if (selectedGame?.id === gameId) {
        refreshSelectedGame(gameId);
      }
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!confirm("Tem certeza que deseja excluir este jogo?")) return;
    try {
      await axios.delete(`${API}/game-registrations/${gameId}`);
      toast.success("Jogo excluido");
      fetchGames();
      setSelectedGame(null);
    } catch (error) {
      toast.error("Erro ao excluir jogo");
    }
  };

  const openEditDialog = (game) => {
    setEditGame({
      title: game.title || "",
      date: game.date || "",
      time: game.time || "19:00",
      location: game.location || "",
      max_players: game.max_players || 18,
      guest_deadline: game.guest_deadline || "",
      scheduled_open: game.scheduled_open || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedGame || !editGame) return;
    setIsSaving(true);
    try {
      await axios.put(`${API}/game-registrations/${selectedGame.id}`, editGame);
      toast.success("Jogo atualizado!");
      setEditDialogOpen(false);
      fetchGames();
      refreshSelectedGame(selectedGame.id);
    } catch (error) {
      toast.error("Erro ao salvar alteracoes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePlayer = async (gameId, playerId) => {
    try {
      await axios.post(`${API}/game-registrations/${gameId}/cancel`, {
        player_id: playerId
      });
      toast.success("Jogador removido");
      refreshSelectedGame(gameId);
      fetchGames();
    } catch (error) {
      toast.error("Erro ao remover jogador");
    }
  };

  const handlePromoteWaitlist = async (gameId) => {
    setIsPromoting(true);
    try {
      const res = await axios.post(`${API}/game-registrations/${gameId}/promote-waitlist`);
      toast.success(res.data.message);
      refreshSelectedGame(gameId);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao promover da lista de espera");
    } finally {
      setIsPromoting(false);
    }
  };

  const refreshSelectedGame = async (gameId) => {
    try {
      const [gameRes, payRes] = await Promise.all([
        axios.get(`${API}/game-registrations/${gameId}`),
        axios.get(`${API}/payments/game/${gameId}/summary`).catch(() => ({ data: { paid_player_ids: [], total_collected: 0 } }))
      ]);
      setSelectedGame(gameRes.data);
      setPaymentSummary(payRes.data);
    } catch (error) {
      console.error("Error refreshing game:", error);
    }
  };

  if (evaluator?.role !== 'admin' && evaluator?.role !== 'tecnico') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-card/50 border-destructive">
          <CardContent className="p-6 text-center">
            <p className="text-lg font-semibold">Acesso Restrito</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const registeredPlayers = selectedGame?.registered_players || [];
  const waitlistPlayers = selectedGame?.waitlist || [];
  const maxPlayers = selectedGame?.max_players || 18;
  const spotsAvailable = maxPlayers - registeredPlayers.length;
  const deadlineStr = selectedGame?.guest_deadline;
  const deadlinePassed = deadlineStr ? new Date() >= new Date(deadlineStr) : false;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  {t('gameRegistrations.title')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Abra inscricoes e gerencie jogadores
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Toggle para liberar inscricoes aos jogadores */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                registrationsEnabled 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <span className={`text-sm font-medium ${
                  registrationsEnabled ? 'text-green-500' : 'text-red-400'
                }`}>
                  {registrationsEnabled ? 'Liberadas' : 'Bloqueadas'}
                </span>
                <button
                  onClick={handleToggleRegistrations}
                  disabled={isTogglingRegistrations}
                  className="focus:outline-none"
                  data-testid="toggle-registrations-btn"
                >
                  {isTogglingRegistrations ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : registrationsEnabled ? (
                    <ToggleRight className="h-6 w-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-red-400" />
                  )}
                </button>
              </div>
              
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-registration-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Abrir Inscricoes
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle>{t('gameRegistrations.newRegistrations')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Titulo</Label>
                      <Input
                        value={newGame.title}
                        onChange={(e) => setNewGame({ ...newGame, title: e.target.value })}
                        placeholder={t('gameRegistrations.defaultGameTitle')}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data</Label>
                        <Input
                          type="date"
                          value={newGame.date}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setNewGame({ ...newGame, date: newDate, guest_deadline: calcMondayDeadline(newDate) });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Horario</Label>
                        <Input
                          type="time"
                          value={newGame.time}
                          onChange={(e) => setNewGame({ ...newGame, time: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Local</Label>
                      <Input
                        value={newGame.location}
                        onChange={(e) => setNewGame({ ...newGame, location: e.target.value })}
                        placeholder={t('gameRegistrations.locationPlaceholder')}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Maximo de Jogadores</Label>
                        <Input
                          type="number"
                          value={newGame.max_players}
                          onChange={(e) => setNewGame({ ...newGame, max_players: parseInt(e.target.value) || 18 })}
                          min={1}
                          max={50}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Timer className="h-3.5 w-3.5" />
                          Prazo Convidados (auto: Seg 12:01)
                        </Label>
                        <Input
                          type="datetime-local"
                          value={newGame.guest_deadline}
                          onChange={(e) => setNewGame({ ...newGame, guest_deadline: e.target.value })}
                          data-testid="guest-deadline-input"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Calculado automaticamente para Segunda 12:01. Membros inscrevem direto, convidados entram na espera ate esse prazo.
                        </p>
                      </div>
                    </div>
                    {/* Agendar abertura */}
                    <div className="space-y-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                      <Label className="flex items-center gap-1.5 text-sm font-medium">
                        <Clock className="h-3.5 w-3.5 text-primary" />
                        Agendar abertura das inscricoes
                      </Label>
                      <Input
                        type="datetime-local"
                        value={newGame.scheduled_open}
                        onChange={(e) => setNewGame({ ...newGame, scheduled_open: e.target.value })}
                        data-testid="scheduled-open-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {newGame.scheduled_open 
                          ? "O jogo sera criado fechado e abrira automaticamente na data/hora definida. Uma notificacao push sera enviada aos jogadores."
                          : "Deixe vazio para abrir as inscricoes imediatamente."}
                      </p>
                    </div>
                    <Button
                      onClick={handleCreateGame}
                      disabled={isCreating}
                      className="w-full"
                      data-testid="create-game-btn"
                    >
                      {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                      {newGame.scheduled_open ? "Agendar Inscricoes" : "Abrir Inscricoes"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Games List */}
          <div className="lg:col-span-1">
            <GameListSidebar
              games={games}
              selectedGame={selectedGame}
              setSelectedGame={setSelectedGame}
              refreshSelectedGame={refreshSelectedGame}
              isLoading={isLoading}
              t={t}
            />
          </div>

          {/* Game Details */}
          <div className="lg:col-span-2">
            <GameDetailPanel
              selectedGame={selectedGame}
              registeredPlayers={registeredPlayers}
              waitlistPlayers={waitlistPlayers}
              maxPlayers={maxPlayers}
              spotsAvailable={spotsAvailable}
              paymentSummary={paymentSummary}
              deadlineStr={deadlineStr}
              deadlinePassed={deadlinePassed}
              isPromoting={isPromoting}
              handleToggleStatus={handleToggleStatus}
              handleDeleteGame={handleDeleteGame}
              handleRemovePlayer={handleRemovePlayer}
              handlePromoteWaitlist={handlePromoteWaitlist}
              openEditDialog={openEditDialog}
              t={t}
            />
          </div>
        </div>
      </main>

      {/* Edit Game Dialog */}
      <EditGameDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        editGame={editGame}
        setEditGame={setEditGame}
        handleSaveEdit={handleSaveEdit}
        isSaving={isSaving}
        calcMondayDeadline={calcMondayDeadline}
        t={t}
      />
    </div>
  );
}
